/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import moment from 'moment'
import { NO_TITLE_FALLBACK, buildDayOccurrenceLayouts, formatCompactSlotLabel, getOccurrenceDurationMinutes } from './calendar_utils'
import { renderEventCardPreview } from './event_card_preview'
import { calendarTheme } from './calendar_theme'

export const CalendarGrid = ({ weekDays, weekOccurrences, hours, calendarHeight, onOpenOccurrence }) => {
  const dayLayouts = useMemo(() => {
    return weekDays.reduce((accumulator, day) => {
      const dayKey = day.format('YYYY-MM-DD')
      accumulator[dayKey] = buildDayOccurrenceLayouts({
        occurrences: weekOccurrences?.[dayKey] || [],
        calendarHeight,
        startHour: calendarTheme.startHour,
        hourRowHeight: calendarTheme.hourRowHeight,
      })
      return accumulator
    }, {})
  }, [calendarHeight, weekDays, weekOccurrences])

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box
        sx={{
          minWidth: 980,
          borderTop: `1px solid ${calendarTheme.colors.gridBorder}`,
          borderLeft: `1px solid ${calendarTheme.colors.gridBorder}`,
          backgroundColor: calendarTheme.colors.background,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `${calendarTheme.timeColumnWidth}px repeat(7, minmax(${calendarTheme.dayColumnMinWidth}px, 1fr))`,
            borderBottom: `1px solid ${calendarTheme.colors.gridBorder}`,
          }}
        >
          <Box sx={{ borderRight: `1px solid ${calendarTheme.colors.gridBorder}`, backgroundColor: calendarTheme.colors.background, boxSizing: 'border-box' }} />
          {weekDays.map((day) => {
            const isToday = day.isSame(moment(), 'day')

            return (
              <Box
                key={day.format('YYYY-MM-DD')}
                sx={{
                  borderRight: `1px solid ${calendarTheme.colors.gridBorder}`,
                  px: 1.5,
                  py: 1.75,
                  textAlign: 'center',
                  backgroundColor: calendarTheme.colors.background,
                  boxSizing: 'border-box',
                }}
              >
                <Typography sx={{ fontSize: '12px', color: calendarTheme.colors.mutedText, fontWeight: 600 }}>
                  {day.format('ddd')}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isToday ? calendarTheme.defaultCardColor : 'transparent',
                    }}
                  >
                    <Typography sx={{ fontSize: '30px', lineHeight: 1.1, fontWeight: 700, color: isToday ? calendarTheme.colors.eventBackground : calendarTheme.colors.headingText }}>
                      {day.format('D')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )
          })}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: `${calendarTheme.timeColumnWidth}px repeat(7, minmax(${calendarTheme.dayColumnMinWidth}px, 1fr))` }}>
          <Box sx={{ borderRight: `1px solid ${calendarTheme.colors.gridBorder}`, boxSizing: 'border-box' }}>
            {hours.map((hour) => (
              <Box
                key={`hour-${hour}`}
                sx={{
                  height: `${calendarTheme.hourRowHeight}px`,
                  borderBottom: `1px solid ${calendarTheme.colors.gridRow}`,
                  px: 1,
                  pt: 0.75,
                  textAlign: 'right',
                  boxSizing: 'border-box',
                }}
              >
                <Typography sx={{ fontSize: '12px', color: '#9ca3af', fontWeight: 600 }}>
                  {moment().hour(hour).minute(0).format('h A')}
                </Typography>
              </Box>
            ))}
          </Box>

          {weekDays.map((day) => {
            const dayKey = day.format('YYYY-MM-DD')
            const dayOccurrences = weekOccurrences?.[dayKey] || []
            const layouts = dayLayouts?.[dayKey] || {}

            return (
              <Box
                key={dayKey}
                sx={{
                  position: 'relative',
                  height: `${calendarHeight}px`,
                  borderRight: `1px solid ${calendarTheme.colors.gridBorder}`,
                  backgroundColor: calendarTheme.colors.background,
                  boxSizing: 'border-box',
                }}
              >
                {hours.map((hour) => (
                  <Box
                    key={`${dayKey}-${hour}`}
                    sx={{
                      height: `${calendarTheme.hourRowHeight}px`,
                      borderBottom: `1px solid ${calendarTheme.colors.gridRow}`,
                      boxSizing: 'border-box',
                    }}
                  />
                ))}

                {dayOccurrences.map((occurrence) => {
                  const previewCards = occurrence?.blockedCards || []
                  const layout = layouts[occurrence.id] || {}
                  const { laneCount = 1, ...style } = layout
                  const durationMinutes = getOccurrenceDurationMinutes(occurrence)
                  const eventSlotCount = occurrence?.event?.slots?.length || 0
                  const isCompactTimedEvent = !occurrence?.slot?.all_day
                    && !occurrence?.slot?.placeholder
                    && durationMinutes <= calendarTheme.compactDurationMinutes
                  const isHalfHourEvent = !occurrence?.slot?.all_day
                    && !occurrence?.slot?.placeholder
                    && durationMinutes <= calendarTheme.halfHourDurationMinutes
                  const isSingleSlotHalfHourEvent = isHalfHourEvent && eventSlotCount === 1 && laneCount === 1

                  return (
                    <Box
                      key={occurrence.id}
                      role="button"
                      tabIndex={0}
                      data-testid={`dynamic-discounts-calendar-event-${occurrence.id}`}
                      onClick={() => onOpenOccurrence(occurrence)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          onOpenOccurrence(occurrence)
                        }
                      }}
                      sx={{
                        position: 'absolute',
                        ...style,
                        borderRadius: '10px',
                        backgroundColor: calendarTheme.colors.eventBackground,
                        border: `1px solid ${calendarTheme.defaultCardColor}`,
                        boxShadow: calendarTheme.shadows.event,
                        px: isHalfHourEvent ? 0.75 : 1.25,
                        py: isHalfHourEvent ? 0.45 : 1,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: isHalfHourEvent ? 'center' : 'flex-start',
                      }}
                    >
                      {isSingleSlotHalfHourEvent ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            minWidth: 0,
                            flexWrap: 'nowrap',
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{ fontSize: '12px', fontWeight: 700, color: calendarTheme.colors.headingText, lineHeight: 1.15, minWidth: 0, flex: '1 1 auto' }}
                            noWrap
                          >
                            {`${occurrence.title || NO_TITLE_FALLBACK}, `}
                            <Typography component="span" sx={{ fontSize: '11px', color: calendarTheme.colors.mutedText, display: 'inline' }} noWrap>
                              {formatCompactSlotLabel(occurrence)}
                            </Typography>
                          </Typography>
                          <Box
                            data-testid={`dynamic-discounts-calendar-event-preview-${occurrence.id}`}
                            sx={{ flexShrink: 0 }}
                          >
                            {renderEventCardPreview(previewCards, 'inline-compact')}
                          </Box>
                        </Box>
                      ) : isHalfHourEvent ? (
                        <Typography
                          component="span"
                          sx={{ fontSize: '12px', fontWeight: 700, color: calendarTheme.colors.headingText, lineHeight: 1.15 }}
                          noWrap
                        >
                          {`${occurrence.title || NO_TITLE_FALLBACK}, `}
                          <Typography component="span" sx={{ fontSize: '11px', color: calendarTheme.colors.mutedText, display: 'inline' }} noWrap>
                            {formatCompactSlotLabel(occurrence)}
                          </Typography>
                        </Typography>
                      ) : isCompactTimedEvent ? (
                        <>
                          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: calendarTheme.colors.headingText, lineHeight: 1.2 }} noWrap>
                            {occurrence.title}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 0.75,
                              mt: 0.5,
                            }}
                          >
                            <Typography sx={{ fontSize: '11px', color: calendarTheme.colors.mutedText, lineHeight: 1.2, minWidth: 0, flex: '1 1 auto' }} noWrap>
                              {occurrence.slotLabel}
                            </Typography>
                            <Box data-testid={`dynamic-discounts-calendar-event-preview-${occurrence.id}`}>
                              {renderEventCardPreview(previewCards, 'compact')}
                            </Box>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: calendarTheme.colors.headingText, lineHeight: 1.2 }} noWrap>
                            {occurrence.title}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: calendarTheme.colors.mutedText, lineHeight: 1.2 }} noWrap>
                            {occurrence.slotLabel}
                          </Typography>
                          <Box data-testid={`dynamic-discounts-calendar-event-preview-${occurrence.id}`}>
                            {renderEventCardPreview(previewCards)}
                          </Box>
                        </>
                      )}
                    </Box>
                  )
                })}
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}
